import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ptBR } from 'date-fns/locale';
import { getMovimentacaoCotista, getMovimentacaoCotistaResumo, MovimentacaoCotistaResumo } from '../../../../services/carteiraDiariaService';
import { MovimentacaoCotistaItem } from '../../../../types/carteiraDiaria';
import { formatBRL, formatQtd, formatDate } from '../../../../utils/formatters';
import { thSx, tdSx, SortHeader, SortDir } from '../../../carteira/components/historicoTableUtils';

const PAGE_SIZE = 30;

export interface MovimentacoesCotistaTabProps {
  fundoId: number;
}

type TipoFiltro = '' | 'APLICACAO' | 'RESGATE';
type CampoData = 'solicitacao' | 'cotizacao' | 'impacto';
type ModoData = 'exata' | 'periodo';

const CAMPO_DATA_LABEL: Record<CampoData, string> = {
  solicitacao: 'Solicitação',
  cotizacao: 'Cotização',
  impacto: 'Impacto',
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${dia}/${mes}/${d.getFullYear()} ${hora}`;
};

const isExcluido = (status: string): boolean => {
  const s = status.toUpperCase();
  return s.startsWith('EXCLU');
};

const toIsoDate = (d: Date | null): string | undefined => {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface PresetData {
  label: string;
  getRange: () => [Date, Date];
}

const PRESETS_DATA: PresetData[] = [
  {
    label: 'Próximos 7 dias',
    getRange: () => {
      const hoje = new Date();
      const fim = new Date(hoje); fim.setDate(fim.getDate() + 7);
      return [hoje, fim];
    },
  },
  {
    label: 'Até o fim do mês',
    getRange: () => {
      const hoje = new Date();
      const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      return [hoje, fim];
    },
  },
  {
    label: 'Próximo mês',
    getRange: () => {
      const hoje = new Date();
      const inicio = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
      const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 2, 0);
      return [inicio, fim];
    },
  },
  {
    label: 'Próximos 3 meses',
    getRange: () => {
      const hoje = new Date();
      const fim = new Date(hoje);
      fim.setMonth(fim.getMonth() + 3);
      return [hoje, fim];
    },
  },
];

const MovimentacoesCotistaTab: React.FC<MovimentacoesCotistaTabProps> = ({ fundoId }) => {
  const [rows, setRows]         = useState<MovimentacaoCotistaItem[]>([]);
  const [count, setCount]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [sortDir, setSortDir]   = useState<SortDir>('desc');

  const [tipoFiltro, setTipoFiltro] = useState<TipoFiltro>('');
  const [campoData, setCampoData]   = useState<CampoData>('impacto');
  const [modoData, setModoData]     = useState<ModoData>('periodo');
  const [dataExata, setDataExata]   = useState<Date | null>(null);
  const [dataInicio, setDataInicio] = useState<Date | null>(null);
  const [dataFim, setDataFim]       = useState<Date | null>(null);

  const [resumo, setResumo] = useState<MovimentacaoCotistaResumo | null>(null);

  const ordering   = sortDir === 'desc' ? '-data_solicitacao' : 'data_solicitacao';
  const totalPages = Math.ceil(count / PAGE_SIZE);

  const temFiltroData = modoData === 'exata' ? !!dataExata : !!dataInicio || !!dataFim;
  const filtrosAtivos = tipoFiltro !== '' || temFiltroData;

  const filtrosData = useMemo(() => {
    const inicioIso = modoData === 'exata' ? toIsoDate(dataExata) : toIsoDate(dataInicio);
    const fimIso     = modoData === 'exata' ? toIsoDate(dataExata) : toIsoDate(dataFim);
    return { dataCampo: campoData, dataInicio: inicioIso, dataFim: fimIso };
  }, [campoData, modoData, dataExata, dataInicio, dataFim]);

  const filtros = useMemo(() => ({
    ...filtrosData,
    tipoOperacao: tipoFiltro || undefined,
  }), [filtrosData, tipoFiltro]);

  const load = useCallback(() => {
    setLoading(true);
    getMovimentacaoCotista(fundoId, page, PAGE_SIZE, ordering, filtros)
      .then(res => {
        setRows(res.results);
        setCount(res.count);
      })
      .catch(() => { setRows([]); setCount(0); })
      .finally(() => { setLoading(false); setInitialLoad(false); });
  }, [fundoId, page, ordering, filtros]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!temFiltroData) { setResumo(null); return; }
    getMovimentacaoCotistaResumo(fundoId, filtrosData)
      .then(setResumo)
      .catch(() => setResumo(null));
  }, [fundoId, filtrosData, temFiltroData]);

  const handleSort = (_f: string) => {
    setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    setPage(1);
  };

  const handleLimparFiltros = () => {
    setTipoFiltro('');
    setCampoData('impacto');
    setModoData('periodo');
    setDataExata(null);
    setDataInicio(null);
    setDataFim(null);
    setPage(1);
  };

  const handlePreset = (preset: PresetData) => {
    const [inicio, fim] = preset.getRange();
    setModoData('periodo');
    setDataExata(null);
    setDataInicio(inicio);
    setDataFim(fim);
    setPage(1);
  };

  return (
    <Box sx={{ backgroundColor: '#F8FAFC' }}>
      {/* Barra de filtros */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <FormControl size="small" sx={{ width: 140 }}>
          <InputLabel>Tipo</InputLabel>
          <Select
            label="Tipo"
            value={tipoFiltro}
            onChange={e => { setTipoFiltro(e.target.value as TipoFiltro); setPage(1); }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="APLICACAO">Aplicação</MenuItem>
            <MenuItem value="RESGATE">Resgate</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ width: 160 }}>
          <InputLabel>Filtrar data por</InputLabel>
          <Select
            label="Filtrar data por"
            value={campoData}
            onChange={e => setCampoData(e.target.value as CampoData)}
          >
            {(Object.keys(CAMPO_DATA_LABEL) as CampoData[]).map(k => (
              <MenuItem key={k} value={k}>{CAMPO_DATA_LABEL[k]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          size="small"
          value={modoData}
          exclusive
          onChange={(_e, v) => { if (v) { setModoData(v); setPage(1); } }}
        >
          <ToggleButton value="periodo" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5 }}>Período</ToggleButton>
          <ToggleButton value="exata" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5 }}>Data exata</ToggleButton>
        </ToggleButtonGroup>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          {modoData === 'exata' ? (
            <DatePicker
              label={`${CAMPO_DATA_LABEL[campoData]} em`}
              value={dataExata}
              onChange={d => { setDataExata(d); setPage(1); }}
              slotProps={{ textField: { size: 'small', sx: { width: 170 } }, field: { clearable: true } }}
            />
          ) : (
            <>
              <DatePicker
                label={`${CAMPO_DATA_LABEL[campoData]} de`}
                value={dataInicio}
                onChange={d => { setDataInicio(d); setPage(1); }}
                slotProps={{ textField: { size: 'small', sx: { width: 170 } }, field: { clearable: true } }}
              />
              <DatePicker
                label={`${CAMPO_DATA_LABEL[campoData]} até`}
                value={dataFim}
                onChange={d => { setDataFim(d); setPage(1); }}
                slotProps={{ textField: { size: 'small', sx: { width: 170 } }, field: { clearable: true } }}
              />
            </>
          )}
        </LocalizationProvider>

        {filtrosAtivos && (
          <Button
            size="small"
            startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
            onClick={handleLimparFiltros}
            sx={{ textTransform: 'none', fontSize: '12px', color: '#8A98A8' }}
          >
            Limpar filtros
          </Button>
        )}
      </Stack>

      {/* Presets de período */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ px: 2, pb: 1.5 }}>
        {PRESETS_DATA.map(preset => {
          const [pInicio, pFim] = preset.getRange();
          const selecionado = modoData === 'periodo'
            && toIsoDate(dataInicio) === toIsoDate(pInicio)
            && toIsoDate(dataFim) === toIsoDate(pFim);
          return (
            <Chip
              key={preset.label}
              label={preset.label}
              size="small"
              onClick={() => handlePreset(preset)}
              sx={{
                fontSize: '11px', cursor: 'pointer',
                fontWeight: selecionado ? 700 : 500,
                backgroundColor: selecionado ? '#2C91AD' : '#EFF6FF',
                color: selecionado ? '#fff' : '#2C91AD',
                border: selecionado ? '1px solid #2C91AD' : '1px solid transparent',
              }}
            />
          );
        })}
      </Stack>

      {/* Resumo por tipo (exclui status Excluído) — só com filtro de data ativo */}
      {temFiltroData && resumo && (
        <Stack direction="row" spacing={1.5} sx={{ px: 2, pb: 1.5 }}>
          <Box sx={{ flex: 1, p: 1.25, borderRadius: 1.5, backgroundColor: '#E7F6EC', border: '1px solid #C7EBD3' }}>
            <Typography sx={{ fontSize: '11px', color: '#29A152', fontWeight: 600, textTransform: 'uppercase' }}>
              Aplicações ({resumo.aplicacao.qtd})
            </Typography>
            <Typography sx={{ fontSize: '15px', color: '#1E293B', fontWeight: 700 }}>
              {formatBRL(resumo.aplicacao.total)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, p: 1.25, borderRadius: 1.5, backgroundColor: '#FDECEC', border: '1px solid #F8CFCF' }}>
            <Typography sx={{ fontSize: '11px', color: '#E51739', fontWeight: 600, textTransform: 'uppercase' }}>
              Resgates ({resumo.resgate.qtd})
            </Typography>
            <Typography sx={{ fontSize: '15px', color: '#1E293B', fontWeight: 700 }}>
              {formatBRL(resumo.resgate.total)}
            </Typography>
          </Box>
        </Stack>
      )}

      <Box sx={{ height: 3 }}>
        {loading && !initialLoad && <LinearProgress sx={{ height: 3 }} />}
      </Box>

      {initialLoad && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography sx={{ py: 3, color: '#8A98A8', textAlign: 'center', fontSize: '13px', fontFamily: 'Inter' }}>
          {filtrosAtivos
            ? 'Nenhuma movimentação encontrada para os filtros selecionados.'
            : 'Sem movimentações de cotistas registradas para este fundo.'}
        </Typography>
      ) : (
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1050 }}>
              <TableHead>
                <TableRow>
                  <SortHeader label="Solicitado em" field="data_solicitacao" currentField="data_solicitacao" currentDir={sortDir} align="left" onSort={handleSort} />
                  <TableCell sx={thSx}>Cotizado em</TableCell>
                  <TableCell sx={thSx}>Impacta em</TableCell>
                  <TableCell sx={thSx}>Tipo</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>Valor</TableCell>
                  <TableCell sx={{ ...thSx, textAlign: 'right' }}>Cotas</TableCell>
                  <TableCell sx={thSx}>Status</TableCell>
                  <TableCell sx={thSx}>Cliente</TableCell>
                  <TableCell sx={thSx}>Criado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const isAplicacao = r.tipo_operacao === 'APLICACAO';
                  const mudouStatus = !!r.status_anterior;
                  const excluido = isExcluido(r.status);
                  const corTipo = excluido ? '#8A98A8' : (isAplicacao ? '#29A152' : '#E51739');
                  return (
                    <TableRow key={r.id} sx={{
                      '&:nth-of-type(even)': { backgroundColor: excluido ? '#F1F1F1' : '#F4F7F9' },
                      opacity: excluido ? 0.55 : 1,
                    }}>
                      <TableCell sx={{ ...tdSx, fontWeight: 500 }}>
                        {r.data_solicitacao ? formatDate(r.data_solicitacao) : '—'}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        {r.data_cotizacao ? formatDate(r.data_cotizacao) : '—'}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        {formatDate(r.data_impacto)}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Chip
                          label={r.tipo_operacao_display}
                          size="small"
                          sx={{
                            height: 20, fontSize: '11px', fontWeight: 600,
                            backgroundColor: excluido ? '#E9E9E9' : (isAplicacao ? '#E7F6EC' : '#FDECEC'),
                            color: corTipo,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{
                        ...tdSx, textAlign: 'right', fontWeight: 600,
                        color: corTipo,
                        textDecoration: excluido ? 'line-through' : 'none',
                      }}>
                        {isAplicacao ? '+' : '−'}{formatBRL(r.valor_total)}
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                        {r.quotas != null ? formatQtd(r.quotas) : '—'}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Tooltip
                          title={mudouStatus
                            ? `Mudou de "${r.status_anterior}" para "${r.status}" em ${formatDateTime(r.status_atualizado_em)}`
                            : `Sem alteração de status desde ${formatDateTime(r.status_atualizado_em)}`}
                          placement="top" arrow
                        >
                          <Typography sx={{
                            fontSize: '12px', fontFamily: 'Inter', cursor: 'default',
                            color: excluido ? '#8A98A8' : '#1E293B',
                            fontStyle: excluido ? 'italic' : 'normal',
                          }}>
                            {r.status}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Tooltip title={r.cliente_documento || ''} placement="top" arrow disableHoverListener={!r.cliente_documento}>
                          <Typography sx={{
                            fontSize: '12px', fontFamily: 'Inter', color: '#1E293B', cursor: 'default',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220,
                          }}>
                            {r.cliente_nome || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: '11px', fontFamily: 'Inter', color: '#8A98A8' }}>
                          {formatDateTime(r.criado_em)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Paginação */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 2, py: 0.75, borderTop: '1px solid #EFF2F5' }}>
            <Typography sx={{ fontSize: '11px', color: '#8A98A8', fontFamily: 'Inter' }}>
              {count} registro{count !== 1 ? 's' : ''}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#6B7A8D', fontFamily: 'Inter', mx: 0.5 }}>
              Página {page} de {totalPages}
            </Typography>
            <Tooltip title="Página anterior">
              <span>
                <IconButton size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)} sx={{ p: 0.5 }}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Próxima página">
              <span>
                <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} sx={{ p: 0.5 }}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MovimentacoesCotistaTab;
